import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import FAQAccordion from '../components/FAQAccordion';
import { FAQPageSchema, BreadcrumbSchema, materialManagementFaqItems } from '../components/JsonLdSchema';

const accessPoints = [
  {
    icon: '💻',
    title: 'Any Device, Any Time',
    desc: 'Access from any laptop or desktop. Your data lives on the cloud — no local servers, no hardware headaches.',
  },
  {
    icon: '🔒',
    title: 'Secure & Encrypted',
    desc: 'Industry-standard encryption prevents even the remotest chance of fraud or mismanagement. Your data, protected.',
  },
  {
    icon: '📊',
    title: 'Real-Time Everywhere',
    desc: 'All data is live regardless of location or time. Multi-location operations stay in perfect sync, always.',
  },
];

const features = [
  {
    icon: '📦',
    title: 'End-to-End Inventory',
    desc: 'Manage indents, purchase orders, receiving, and stock tracking — every step from procurement to shelf in one system.',
  },
  {
    icon: '🧾',
    title: 'Recipe & Yield Management',
    desc: 'Build recipes with semi-finished goods and yield percentages. Costing flows automatically from ingredients to dishes.',
  },
  {
    icon: '✉️',
    title: 'Authorization Workflows',
    desc: 'Approval emails sent to pre-defined IDs at every step. Full audit trail so nothing moves without the right sign-off.',
  },
  {
    icon: '📈',
    title: 'Real-Time Online Reports',
    desc: 'Backdated entries recalculate monthly costings. Export reports to Excel, Word, and more with one click.',
  },
  {
    icon: '🏢',
    title: 'Multi-Location Ready',
    desc: 'Unparalleled efficiency across departments and outlets. One dashboard, all your locations, complete visibility.',
  },
  {
    icon: '🔗',
    title: 'Tally Integration',
    desc: 'Interfaced with Tally and other accounts-receivable software. Reconciliation is automatic, not manual.',
  },
  {
    icon: '👥',
    title: 'Role-Based Security',
    desc: 'Advanced multi-level access authentication. Each team member sees only what they need — not a byte more.',
  },
  {
    icon: '☁️',
    title: 'Zero Server Costs',
    desc: 'No server issues, no software updates, no upgrade fees. Simple monthly model — pay only for what you use.',
  },
];

const highlights = [
  { value: '99.9%', label: 'Uptime SLA' },
  { value: 'Cloud', label: 'No Local Server' },
  { value: 'Real-Time', label: 'Live Reports' },
  { value: 'Multi-Site', label: 'All Locations' },
];

function FeatureCard({ item }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? '#fff' : '#f9fafb',
        border: `1px solid ${hov ? '#CC0000' : '#e5e7eb'}`,
        borderRadius: 14,
        padding: '28px 24px',
        transition: 'all 0.22s',
        boxShadow: hov ? '0 6px 24px rgba(204,0,0,0.10)' : 'none',
      }}
    >
      <div style={{
        width: 52, height: 52, borderRadius: 14,
        background: hov ? '#fef2f2' : '#f3f4f6',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 26, marginBottom: 16, transition: 'background 0.22s',
      }}>
        {item.icon}
      </div>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 8 }}>{item.title}</h3>
      <p style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.65 }}>{item.desc}</p>
    </div>
  );
}

/* ── CSS dashboard mockup ── */
function DashboardMockup() {
  return (
    <div style={{
      background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb',
      boxShadow: '0 20px 60px rgba(0,0,0,0.12)', overflow: 'hidden',
      fontFamily: 'Inter, sans-serif',
    }}>
      {/* Title bar */}
      <div style={{ background: '#111827', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#CC0000', display: 'block' }} />
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#f59e0b', display: 'block' }} />
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#10b981', display: 'block' }} />
        <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 8 }}>DPOS Inventory — Inventory Dashboard</span>
      </div>
      {/* Header bar */}
      <div style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb', padding: '10px 16px', display: 'flex', gap: 12 }}>
        {['Dashboard', 'Procurement', 'Stock', 'Reports'].map((t, i) => (
          <span key={t} style={{
            fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 6,
            background: i === 0 ? '#CC0000' : 'transparent',
            color: i === 0 ? '#fff' : '#6b7280',
            cursor: 'pointer',
          }}>{t}</span>
        ))}
      </div>
      {/* Stat cards */}
      <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
        {[
          { label: 'Total Items', val: '1,248', color: '#CC0000' },
          { label: 'Pending POs', val: '14', color: '#f59e0b' },
          { label: 'Low Stock Alerts', val: '6', color: '#10b981' },
        ].map(s => (
          <div key={s.label} style={{ background: '#f9fafb', borderRadius: 8, padding: '12px 10px', borderLeft: `3px solid ${s.color}` }}>
            <p style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>{s.val}</p>
            <p style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>{s.label}</p>
          </div>
        ))}
      </div>
      {/* Mini table */}
      <div style={{ padding: '0 16px 16px' }}>
        <p style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Recent Indents</p>
        {[
          { item: 'Tomato Puree', qty: '20 kg', status: 'Approved', c: '#10b981' },
          { item: 'Basmati Rice', qty: '50 kg', status: 'Pending', c: '#f59e0b' },
          { item: 'Chicken Breast', qty: '30 kg', status: 'Received', c: '#6366f1' },
        ].map(r => (
          <div key={r.item} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f3f4f6' }}>
            <span style={{ fontSize: 12, color: '#374151', fontWeight: 500 }}>{r.item}</span>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>{r.qty}</span>
            <span style={{ fontSize: 10, fontWeight: 700, color: r.c, background: r.c + '15', padding: '2px 8px', borderRadius: 100 }}>{r.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MaterialManagement() {
  return (
    <main style={{ paddingTop: 64 }}>
      <SEOHead
        title="Restaurant Inventory Software | DPOS Inventory"
        description="DPOS Inventory is restaurant inventory software: purchase orders, receiving, stock tracking, and recipe costing, with real-time visibility across outlets."
        canonical="/material-management"
      />
      <FAQPageSchema items={materialManagementFaqItems} />
      <BreadcrumbSchema items={[
        { name: 'Home', path: '/' },
        { name: 'DPOS Inventory' },
      ]} />

      {/* Hero */}
      <section style={{ padding: '72px 24px 80px', background: 'linear-gradient(135deg, #fff 0%, #fef2f2 100%)', borderBottom: '1px solid #fecaca' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'center' }} className="mms-hero-grid">
          <div>
            <div style={{
              display: 'inline-block', background: '#fef2f2', color: '#CC0000',
              fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
              padding: '4px 14px', borderRadius: 100, marginBottom: 20, border: '1px solid #fecaca',
            }}>ERP Module</div>
            <h1 style={{ fontSize: 'clamp(28px, 3.5vw, 48px)', fontWeight: 800, color: '#111827', marginBottom: 16, lineHeight: 1.15 }}>
              DPOS Inventory<br />Restaurant Inventory Software
            </h1>
            <p style={{ fontSize: 16, color: '#6b7280', lineHeight: 1.75, marginBottom: 32, maxWidth: 500 }}>
              A web-based ERP module built for F&B operations. From indent to purchase to receiving to stock tracking — manage every step with cloud precision and real-time visibility.
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Link to="/contact" style={{
                background: '#CC0000', color: '#fff', padding: '12px 28px',
                borderRadius: 8, fontWeight: 600, fontSize: 15, display: 'inline-flex', alignItems: 'center', gap: 8,
              }}>Book a Free Demo →</Link>
              <Link to="/contact" style={{
                background: '#fff', color: '#111827', padding: '12px 28px',
                borderRadius: 8, fontWeight: 600, fontSize: 15, border: '1px solid #e5e7eb',
              }}>Talk to Sales</Link>
            </div>
          </div>
          <div className="mms-hero-visual">
            <DashboardMockup />
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section style={{ background: '#111827', padding: '40px 24px' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 24 }}>
          {highlights.map(h => (
            <div key={h.label} style={{ textAlign: 'center' }}>
              <p style={{ fontSize: 24, fontWeight: 800, color: '#CC0000', marginBottom: 4 }}>{h.value}</p>
              <p style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{h.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Anywhere Access */}
      <section style={{ padding: '80px 24px', background: '#fff' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 56 }}>
            <h2 style={{ fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 800, color: '#111827', marginBottom: 12 }}>
              Anytime, Anywhere — DPOS Inventory
            </h2>
            <p style={{ fontSize: 16, color: '#6b7280', maxWidth: 560, margin: '0 auto', lineHeight: 1.7 }}>
              Your entire supply chain in the cloud. No local servers. No software upgrades. Just reliable access with up to 99.9% uptime.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
            {accessPoints.map(a => (
              <div key={a.title} style={{
                background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 16,
                padding: '36px 28px', textAlign: 'center',
              }}>
                <div style={{
                  width: 72, height: 72, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #fef2f2, #fee2e2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 32, margin: '0 auto 20px',
                }}>
                  {a.icon}
                </div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 10 }}>{a.title}</h3>
                <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.7 }}>{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section style={{ padding: '0 24px 80px', background: '#f9fafb' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', paddingTop: 80, marginBottom: 56 }}>
            <h2 style={{ fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 800, color: '#111827', marginBottom: 12 }}>
              DPOS Inventory Features
            </h2>
            <p style={{ fontSize: 16, color: '#6b7280', maxWidth: 520, margin: '0 auto', lineHeight: 1.7 }}>
              Everything your F&B operation needs to control costs, reduce waste, and keep every outlet stocked.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 20 }}>
            {features.map(f => <FeatureCard key={f.title} item={f} />)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '40px 24px', background: 'linear-gradient(135deg, #CC0000 0%, #A80000 100%)', textAlign: 'center' }}>
        <div style={{ maxWidth: 640, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 800, color: '#fff', marginBottom: 16 }}>
            Ready to take control of your supply chain?
          </h2>
          <p style={{ fontSize: 16, color: '#fecaca', marginBottom: 32, lineHeight: 1.7 }}>
            Join hundreds of restaurants and hotels using DPOS Inventory to cut waste, control costs, and run tighter operations.
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" style={{
              background: '#fff', color: '#CC0000', padding: '13px 32px',
              borderRadius: 8, fontWeight: 700, fontSize: 15,
            }}>Contact for Free Demo</Link>
            <Link to="/contact" style={{
              background: 'transparent', color: '#fff', padding: '13px 32px',
              borderRadius: 8, fontWeight: 600, fontSize: 15, border: '1px solid rgba(255,255,255,0.4)',
            }}>Talk to Sales</Link>
          </div>
        </div>
      </section>

      <style>{`
        @media(max-width: 900px) {
          .mms-hero-grid { grid-template-columns: 1fr !important; }
          .mms-hero-visual { display: none; }
        }
      `}</style>

      <section style={{ padding: '56px 24px', background: '#fff' }}>
        <div style={{ maxWidth: 780, margin: '0 auto' }}>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: '#111827', marginBottom: 20 }}>Guides on Inventory</h2>
          <ul style={{ display: 'flex', flexDirection: 'column', gap: 12, listStyle: 'none', padding: 0 }}>
            <li><Link to="/blog/how-to-choose-restaurant-inventory-software" style={{ color: '#CC0000', fontWeight: 600 }}>How to choose restaurant inventory software in India</Link></li>
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ padding: '80px 24px', background: '#f9fafb' }}>
        <div style={{ maxWidth: 780, margin: '0 auto' }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: '#CC0000', textTransform: 'uppercase', letterSpacing: '0.12em', textAlign: 'center', marginBottom: 6 }}>
            Frequently Asked Questions
          </p>
          <h2 style={{ fontSize: 'clamp(24px, 3vw, 36px)', fontWeight: 800, color: '#111827', textAlign: 'center', marginBottom: 40 }}>
            DPOS Inventory, Answered
          </h2>
          <FAQAccordion items={materialManagementFaqItems} />
        </div>
      </section>
    </main>
  );
}
